import { setCustomColors } from '@monthio/react-components'
import { captureMessage } from '@sentry/react'
import { parseToHsl } from 'polished'

import type { CustomColors } from '../types/authenticateSmartcheckSessionDto'

export const getCustomColorMap = ({
	primaryColor,
	secondaryColor,
}: CustomColors) => {
	return {
		...toPartialColorMap(primaryColor, primaryColorModifiers),
		...toPartialColorMap(secondaryColor, secondaryColorMaps),
	}
}

const COLOR_BRAND_KEYS = [
	'50',
	'100',
	'200',
	'300',
	'400',
	'500',
	'600',
	'700',
	'800',
	'900',
] as const

/**
 * Applies custom brand colors to the DOM and react-components.
 * Colors should be hex values without '#' prefix (e.g. 'FF5733').
 */
export const applyCustomColors = (customColors: CustomColors) => {
	const rootElement = document.documentElement
	const colorMap = getCustomColorMap({
		primaryColor: customColors.primaryColor
			? `#${customColors.primaryColor}`
			: undefined,
		secondaryColor: customColors.secondaryColor
			? `#${customColors.secondaryColor}`
			: undefined,
	})

	if (!rootElement) {
		captureMessage('Could not set custom colors: root element not found', {
			tags: {
				module: 'custom-colors',
			},
		})
	}
	// for the design system components
	Object.entries(colorMap).forEach(([colorKey, colorValue]) => {
		rootElement?.style.setProperty(`--color-brand-${colorKey}`, colorValue)
	})
	// for the react-components
	setCustomColors(colorMap)
}

export const removeCustomColors = () => {
	const rootElement = document.documentElement
	const allKeys = [...COLOR_BRAND_KEYS.map((key) => `--color-brand-${key}`)]
	allKeys.forEach((key) => {
		rootElement?.style.removeProperty(key)
	})
}

type ColorValue =
	| '50'
	| '100'
	| '200'
	| '300'
	| '400'
	| '500'
	| '600'
	| '700'
	| '800'
	| '900'
type ColorMap = { [key in ColorValue]?: string }
interface ColorModifier {
	targetValue: ColorValue
	lightnessModifier: number
}

const primaryColorModifiers: ColorModifier[] = [
	{ targetValue: '500', lightnessModifier: 0 },
	{ targetValue: '600', lightnessModifier: -5 },
	{ targetValue: '700', lightnessModifier: -10 },
	{ targetValue: '800', lightnessModifier: -20 },
	{ targetValue: '900', lightnessModifier: -30 },
]

const secondaryColorMaps: ColorModifier[] = [
	{ targetValue: '50', lightnessModifier: 3 },
	{ targetValue: '100', lightnessModifier: 0 },
	{ targetValue: '200', lightnessModifier: -5 },
	{ targetValue: '300', lightnessModifier: -10 },
	{ targetValue: '400', lightnessModifier: -20 },
]

const toPartialColorMap = (
	baseColor: string | undefined,
	colorModifiers: ColorModifier[],
): ColorMap =>
	baseColor === undefined
		? {}
		: Object.fromEntries(
				colorModifiers.map(({ targetValue, lightnessModifier }) => [
					targetValue,
					modifyLightness(baseColor, lightnessModifier),
				]),
			)

const modifyLightness = (baseColor: string, lightnessModifier: number) => {
	if (
		CSS.supports(
			`color: hsl(from ${baseColor} h s calc(l + ${lightnessModifier}))`,
		)
	) {
		return `hsl(from ${baseColor} h s calc(l + ${lightnessModifier}))`
	}
	const hsl = parseToHsl(baseColor)
	const lightness = hsl.lightness * 100 + lightnessModifier
	const clampedLightness = Math.max(0, Math.min(100, lightness))

	return `hsl(${hsl.hue}, ${hsl.saturation * 100}%, ${clampedLightness}%)`
}
